import React from "react";
import t from "../../../i18n";
import formatDate from "@ui/i18n/formatDate";
import { styled } from "@linaria/react";
import { color, fonts } from "@ui";
import type { ActivityIndexJSON } from "../../../api/useActivities";
import { ErrorWarningFill } from "@ui/IconV2/react";
import TooltipV2 from "@ui/TooltipV2";

export default function Summary({
  patchResponse,
}: {
  patchResponse: ActivityIndexJSON["SNAPSHOT_UPSERTED"]["properties"];
}) {
  const pointCount =
    patchResponse.conflictingSnapshotsDiffCount +
    patchResponse.mergedSnapshotsCount +
    patchResponse.ignoredSnapshotsCount +
    patchResponse.newSnapshotsCount;

  const period = React.useMemo(() => {
    if (patchResponse.startPeriod && patchResponse.endPeriod) {
      return new Date(patchResponse.startPeriod).getTime() ===
        new Date(patchResponse.endPeriod).getTime()
        ? formatDate(patchResponse.startPeriod, "date", null)
        : `${formatDate(patchResponse.startPeriod, "date", null)} - ${formatDate(
            patchResponse.endPeriod,
            "date",
            null,
          )}`;
    }
    return null;
  }, [patchResponse.startPeriod, patchResponse.endPeriod]);

  return (
    <>
      <Line>
        <LineTitle>{t("sources.patchModal.data.sensorCount")}</LineTitle>
        {patchResponse.sensorCount}
      </Line>
      {patchResponse.startPeriod && patchResponse.endPeriod && (
        <Line>
          <>
            <LineTitle>{t("sources.patchModal.data.period")}</LineTitle>
            {period}
          </>
        </Line>
      )}
      <Line>
        <LineTitle>{t("sources.patchModal.data.snapshotCount")}</LineTitle>
        {pointCount}
        <DryInfoLine>
          <InfoContainer>
            {patchResponse.conflictingSnapshotsDiffCount > 0 && (
              <Line>
                <LineTitle>{t("sources.patchModal.source.conflicting")}</LineTitle>
                <ArrowLessToolTip text={t("sources.patchModal.source.conflicting.tooltip")}>
                  <CountContainer>
                    {patchResponse.conflictingSnapshotsDiffCount} <ErrorIcon />
                  </CountContainer>
                </ArrowLessToolTip>
              </Line>
            )}
            {patchResponse.mergedSnapshotsCount > 0 && (
              <Line>
                <LineTitle>{t("sources.patchModal.source.merged")}</LineTitle>
                <ArrowLessToolTip text={t("sources.patchModal.source.merged.tooltip")}>
                  <CountContainer>
                    {patchResponse.mergedSnapshotsCount} <NotificationIcon />
                  </CountContainer>
                </ArrowLessToolTip>
              </Line>
            )}
            {patchResponse.ignoredSnapshotsCount > 0 && (
              <Line>
                <LineTitle>{t("sources.patchModal.source.ignored")}</LineTitle>
                <ArrowLessToolTip text={t("sources.patchModal.source.ignored.tooltip")}>
                  <CountContainer>
                    {patchResponse.ignoredSnapshotsCount} <NotificationIcon />
                  </CountContainer>
                </ArrowLessToolTip>
              </Line>
            )}
            {patchResponse.newSnapshotsCount > 0 && (
              <Line>
                <LineTitle>{t("sources.patchModal.source.new")}</LineTitle>
                <CountContainer>{patchResponse.newSnapshotsCount}</CountContainer>
              </Line>
            )}
          </InfoContainer>
        </DryInfoLine>
      </Line>
    </>
  );
}

const InfoContainer = styled.div`
  font: ${fonts.body2};
  margin: 0 20px;
`;

const CountContainer = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${color.carbon800};
  margin-left: 2px;
`;

const NotificationIcon = styled(ErrorWarningFill)`
  margin-left: 5px;
  height: 14px;
  width: 14px;
  color: ${color.info};
`;

const ErrorIcon = styled(ErrorWarningFill)`
  > :first-child {
    transform: translateY(0.2em);
  }
  height: 16px;
  width: 16px;
  color: ${color.oxygen400};
`;

const Line = styled.div`
  color: ${color.carbon800};
  padding: 5px 0;
`;

const LineTitle = styled.span`
  color: ${color.carbon900};
  font-weight: bold;
  :after {
    padding: 0 3px;
    content: " : ";
  }
`;

const DryInfoLine = styled(Line)`
  margin: 0 0 10px -10px;
`;

const ArrowLessToolTip = ({ text, children }: { text: string; children: React.ReactElement }) => (
  <TooltipV2 placement="right" overlay={text}>
    {children}
  </TooltipV2>
);
